import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import _, { chain, partial, split } from 'lodash';

import { ENTERPRISE_CARD_PARTNER_EBC_ID } from '../../../../constants/ebon';
import { KYC_USE_CASE_WAITING_TRANSACTION } from '../../../../services/kyc/constants';
import {
    PENDING,
    VALIDATED,
    CANCELED,
    INSUFFICIENT_FOUND,
    WAITING_TRANSACTION,
    WAITING_STOCK
} from '../../../../services/checkout/constants';
import { PATH } from '../../../../constants/path';
import { CONTEXT_ROOT } from '../../../../constants';
import { MODAL_TYPE_KYC, MODAL_TYPE_LOGGED_OUT } from '../../../../services/modal/constants';

import { showModal } from '../../../../services/modal/actions';
import { fetchCheckoutDetails } from '../../../../services/checkout/actions';
import { fetchEbonOrderByIdWithCode } from '../../../../services/ebon/actions';
import { fetchUserInfo } from '../../../../services/account/myprofile/actions';
import { logoutUser } from '../../../../services/authentication/logout/actions';
import { fetchUserBalance } from '../../../../services/account/balance/actions';

import { getPartner } from '../../../../services/partner/selector';

import { convertHtmlToReactDomWithParams } from '../../../../utils/htmlUtils';

import { isTokenExpired } from '../../../../services/authentication/utils';

import Stepper from '../Stepper';
import { Logo } from '../../../icons/Logo';
import HeadComponent from '../../../HeadComponent';
import LocalizedNavLink from '../../../router/LocalizedNavLink';
import { LoadingComponent } from '../../../loading/LoadingComponent';
import ConfirmationEbonPageKycBloc from './kyc/ConfirmationEbonPageKycBloc';
import ConfirmationEbonPageMessageBloc from './message/ConfirmationEbonPageMessageBloc';

import './ConfirmationEbonPage.scss';

const ConfirmationEbonPage = ({
    i18n,
    history,
    BRAND_NAME,
    user,
    token,
    isAuthenticated,
    fetchEbonOrderByIdWithCode,
    fetchUserInfo,
    fetchCheckoutDetails,
    orderId,
    confirmedOrder,
    fetchOrder,
    partner,
    logoutUser,
    showModal,
    checkoutSessionId,
    ebonOrderState,
    fetchingDetails,
    fetchUserBalance
}) => {
    const [kyc, setKyc] = useState(false);
    const [kycValidated, setKycValidated] = useState(false);
    const [pendingInterval, setPendingInterval] = useState(null);
    const [detailsInterval, setDetailsInterval] = useState(null);

    const isEnterpriseCard = partner && partner.ebcId === ENTERPRISE_CARD_PARTNER_EBC_ID;

    const loading =
        fetchOrder ||
        (!!checkoutSessionId && (fetchingDetails || !ebonOrderState || ebonOrderState === PENDING));

    const orderStateOk =
        (!checkoutSessionId && confirmedOrder && confirmedOrder.state === VALIDATED) ||
        (!!checkoutSessionId && ebonOrderState && ebonOrderState === VALIDATED);

    useEffect(() => {
        if (isMemberAuthenticated()) {
            fetchEbonOrderByIdWithCode(orderId);
            fetchUserInfo();
        }
    }, []);

    useEffect(() => {
        checkAuthentication();
    });

    const isMemberAuthenticated = () => !isTokenExpired(token) && isAuthenticated;

    const checkAuthentication = () => {
        if (!isMemberAuthenticated()) {
            new Promise((resolve, reject) => {
                history.push(PATH.HOME_PATH);
                resolve();
            }).then(() =>
                logoutUser(() =>
                    showModal(MODAL_TYPE_LOGGED_OUT, {
                        id: 'logged-out-modal'
                    })
                )
            );
        }
    };

    /* Get checkout order details */
    useEffect(() => {
        if (checkoutSessionId) {
            fetchCheckoutDetails(orderId);
        }
    }, [checkoutSessionId]);

    // Delay balance fetching because balance update is not instantaneous on server side
    useEffect(() => {
        setTimeout(() => {
            fetchUserBalance();
        }, 1000);
    }, []);

    /* Handle order states */
    useEffect(() => {
        if (checkoutSessionId) {
            if (!ebonOrderState || ebonOrderState === PENDING || ebonOrderState === WAITING_STOCK) {
                if (!pendingInterval) {
                    setPendingInterval(
                        setInterval(() => {
                            fetchCheckoutDetails(orderId);
                        }, 5000)
                    );
                    return () => {
                        clearInterval(pendingInterval);
                    };
                }
            }

            if (!kycValidated && ebonOrderState && ebonOrderState === WAITING_TRANSACTION) {
                clearInterval(pendingInterval);
                clearInterval(detailsInterval);
                setKyc(true);
                showModal(MODAL_TYPE_KYC, {
                    hideCloseButton: true,
                    useCase: KYC_USE_CASE_WAITING_TRANSACTION
                });
            }

            if (ebonOrderState === CANCELED || ebonOrderState === INSUFFICIENT_FOUND) {
                clearInterval(pendingInterval);
                clearInterval(detailsInterval);
                history.push(
                    `${PATH.EBON_ERROR_PATH}-${partner.id}?paiement=error&orderId=${orderId}`
                );
            }

            if (ebonOrderState === VALIDATED) {
                clearInterval(pendingInterval);
                clearInterval(detailsInterval);
            }

            return () => {
                clearInterval(pendingInterval);
                clearInterval(detailsInterval);
            };
        }
    }, [ebonOrderState]);

    return (
        <div className="template template-primary flex-container-v">
            <div className="entete minimal" id="entete">
                <div className="flex-container-v">
                    <header className="tetedepage conteneur flex-container-h flex-center-alt">
                        <LocalizedNavLink to={PATH.HOME_PATH} title={BRAND_NAME} className="logo">
                            <Logo />
                        </LocalizedNavLink>
                        <Stepper steps={i18n.STEPS} current={4} />
                    </header>
                </div>
            </div>
            <main id="p-ebon-confirmation" className="conteneur conteneur-mobile">
                <HeadComponent
                    title={i18n.CONFIRMATION.TITLE}
                    description={i18n.DESCRIPTION}
                    canonicalLink={`${CONTEXT_ROOT}${PATH.EBON_CONFIRMATION_PATH}`}
                />
                <Stepper steps={i18n.STEPS} current={4} cssClass="mobileDisplay" />

                <div className="congratulation-container">
                    {loading && !kycValidated && (
                        <React.Fragment>
                            <LoadingComponent />
                            <p className="pending-message">
                                {convertHtmlToReactDomWithParams(i18n.CONFIRMATION.PENDING_MESSAGE)}
                            </p>
                        </React.Fragment>
                    )}

                    <ConfirmationEbonPageKycBloc
                        orderId={orderId}
                        fetchOrder={fetchOrder}
                        ebonOrderState={ebonOrderState}
                        kyc={kyc}
                        setKyc={setKyc}
                        kycValidated={kycValidated}
                        setKycValidated={setKycValidated}
                        detailsInterval={detailsInterval}
                        setDetailsInterval={setDetailsInterval}
                    />

                    {!kyc && !loading && orderStateOk && (
                        <ConfirmationEbonPageMessageBloc
                            partner={partner}
                            isEnterpriseCard={isEnterpriseCard}
                            sessionEmail={user.email}
                            confirmedOrder={confirmedOrder}
                        />
                    )}
                </div>
            </main>
        </div>
    );
};

ConfirmationEbonPage.propTypes = {
    i18n: PropTypes.object,
    history: PropTypes.object,
    BRAND_NAME: PropTypes.string,
    user: PropTypes.object,
    fetchEbonOrderByIdWithCode: PropTypes.func,
    orderId: PropTypes.string,
    partner: PropTypes.object,
    ebonDiscount: PropTypes.object,
    ebonList: PropTypes.array
};

const mapStateToProps = (state, ownProps) => {
    const search = ownProps.location.search;
    const params =
        search &&
        chain(search)
            .replace('?', '') // a=toto
            .split('&') // ["a=toto","c=bobo","f=coco"]
            .map(partial(split, _, '=', 2)) // [["a","toto"],["c","bobo"],["f","coco"]]
            .fromPairs() // {"a":"toto","c":"bobo","f":"coco"}
            .value();
    const orderId = params.orderId;

    const confirmedOrder = state.ebon.confirmedOrder;
    const partner = getPartner(state, confirmedOrder.partnerId);

    return {
        i18n: state.i18n.page.EBON,
        BRAND_NAME: state.i18n.common.BRAND_NAME,
        token: state.authentication.token,
        isAuthenticated: state.authentication.isAuthenticated,
        user: state.account.user,
        confirmedOrder: confirmedOrder,
        fetchOrder: state.ebon.fetchOrder,
        orderId,
        partner,
        checkoutSessionId: params['cko-session-id'],
        ebonOrderState: state.checkout.details ? state.checkout.details.ebonOrderState : null,
        fetchingDetails: state.checkout.fetchingDetails
    };
};

const mapDispatchToProps = {
    fetchEbonOrderByIdWithCode,
    fetchUserInfo,
    logoutUser,
    showModal,
    fetchCheckoutDetails,
    fetchUserBalance
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ConfirmationEbonPage));



// WEBPACK FOOTER //
// ./src/components/pages/ebon/confirmation/ConfirmationEbonPage.js